import React, { useState, useEffect, useCallback } from 'react';
import { naytiveAPI } from '../services/api';

// Offline deals + installment billing. Onboard a client, build a payment
// schedule where EACH payment has its own included items, and send each
// milestone's Stripe link. Paying a milestone activates that milestone's items.

const LEGAL_TYPES = ['delaware_c_corp', 'delaware_llc', 'wyoming_c_corp', 'wyoming_llc',
  'india_pvt_ltd', 'india_llp', 'uae_freezone', 'uae_mainland', 'singapore_pte_ltd', 'uk_ltd', 'other'];
const ITEM_KINDS = ['compliance_pro', 'condition', 'filing', 'service', 'custom'];

const box = { border: '1px solid #E5E7EB', borderRadius: 10, background: '#fff' };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const btn = { padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0d7a83', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const btnGhost = { ...btn, background: '#fff', color: '#374151', border: '1px solid #D1D5DB' };
const lab = { fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: '#6B7280', display: 'block', marginBottom: 4 };
const pill = (s) => ({ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
  background: s === 'paid' ? '#e2f3e9' : s === 'link_sent' ? '#e0f0f1' : s === 'overdue' ? '#f8e5e3' : '#eef1f4',
  color: s === 'paid' ? '#1c9455' : s === 'link_sent' ? '#0d7a83' : s === 'overdue' ? '#c0433a' : '#5f6d78' });

const blankItem = () => ({ kind: 'filing', label: '', refCode: '' });
const blankMs = () => ({ amount: '', dueDate: '', description: '', items: [] });

export default function Deals() {
  const [deals, setDeals] = useState(null);
  const [sel, setSel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await naytiveAPI.listDeals(); setDeals(r.data?.items || []); }
    catch { setDeals([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDeal = async (id) => {
    try { const r = await naytiveAPI.getDeal(id); setSel(r.data); }
    catch { alert('Could not load the deal'); }
  };

  const sendLink = async (msId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await naytiveAPI.sendMilestoneLink(msId);
      alert(r.data?.emailed ? 'Payment link created and emailed to the client.' : 'Link created, but the email could not be sent (check SMTP).');
      if (sel) openDeal(sel.deal.id);
      load();
    } catch (err) { alert(err.response?.data?.message || 'Could not send the link'); }
    finally { setBusy(false); }
  };
  const copyLink = (url) => { try { navigator.clipboard.writeText(url); alert('Link copied.'); } catch { window.prompt('Copy link:', url); } };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Deals &amp; billing</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Offline deals, per-payment inclusions, and Stripe payment links.</p>
        </div>
        <button style={btn} onClick={() => setShowForm((v) => !v)}>{showForm ? 'Close' : '+ New deal'}</button>
      </div>

      {showForm && <NewDeal onDone={() => { setShowForm(false); load(); }} />}

      <div style={{ ...box, overflow: 'hidden', marginTop: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 90px 120px 120px 90px', gap: 10, padding: '10px 14px', background: '#f8fafb', fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#84929c' }}>
          <span>Deal</span><span>Client</span><span>Value</span><span>Progress</span><span>Next due</span><span></span>
        </div>
        {deals == null ? <div style={{ padding: 16, color: '#9CA3AF' }}>Loading…</div>
          : deals.length === 0 ? <div style={{ padding: 16, color: '#9CA3AF' }}>No deals yet. Create one with “New deal”.</div>
          : deals.map((d) => (
            <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 90px 120px 120px 90px', gap: 10, padding: '12px 14px', borderTop: '1px solid #F3F4F6', alignItems: 'center', fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: '#111827' }}>{d.title} <span style={pill(d.status)}>{d.status}</span></span>
              <span style={{ color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.client_email || '—'}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{d.total_value ? `${d.currency} ${Number(d.total_value).toLocaleString('en-US')}` : '—'}</span>
              <span style={{ color: '#5f6d78' }}>{d.paid}/{d.milestones} paid</span>
              <span style={{ color: d.next_due ? '#111827' : '#9CA3AF' }}>{d.next_due ? String(d.next_due).slice(0, 10) : '—'}</span>
              <button style={btnGhost} onClick={() => openDeal(d.id)}>Open</button>
            </div>
          ))}
      </div>

      {sel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }} onClick={() => setSel(null)}>
          <div style={{ width: 'min(560px,100%)', background: '#fff', height: '100%', overflowY: 'auto', padding: 22 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 19 }}>{sel.deal.title}</h2>
                <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 13 }}>{sel.deal.client_email}{sel.deal.entity_name ? ` · ${sel.deal.entity_name}` : ''}</p>
              </div>
              <button style={btnGhost} onClick={() => setSel(null)}>Close</button>
            </div>
            {sel.deal.notes && <p style={{ fontSize: 13, color: '#374151', background: '#f8fafb', padding: '10px 12px', borderRadius: 8, marginTop: 12 }}>{sel.deal.notes}</p>}

            <h3 style={{ fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: '#84929c', margin: '20px 0 8px' }}>Payment schedule</h3>
            {sel.milestones.map((m) => {
              const its = sel.items.filter((it) => it.milestone_id === m.id);
              return (
                <div key={m.id} style={{ ...box, padding: 12, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{m.currency} {Number(m.amount).toLocaleString('en-US')} <span style={pill(m.status)}>{m.status}</span></div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{m.due_date ? `Due ${String(m.due_date).slice(0, 10)}` : 'No date'}{m.description ? ` · ${m.description}` : ''}</div>
                    </div>
                    {m.status !== 'paid' && m.status !== 'void' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {m.checkout_url && <button style={btnGhost} onClick={() => copyLink(m.checkout_url)}>Copy link</button>}
                        <button style={btn} disabled={busy} onClick={() => sendLink(m.id)}>{m.status === 'link_sent' ? 'Resend link' : 'Send link'}</button>
                      </div>
                    )}
                    {m.status === 'paid' && <span style={{ fontSize: 12, color: '#1c9455' }}>Paid {m.paid_at ? String(m.paid_at).slice(0, 10) : ''}</span>}
                  </div>
                  {its.length > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #E5E7EB' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 4 }}>Included in this payment</div>
                      {its.map((it) => (
                        <div key={it.id} style={{ fontSize: 12.5, display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                          <span>{it.label} <span style={{ fontSize: 11, color: '#84929c' }}>({it.kind}{it.ref_code ? `:${it.ref_code}` : ''})</span></span>
                          <span style={{ fontSize: 11, color: it.activated_at ? '#1c9455' : '#9CA3AF' }}>{it.activated_at ? 'activated' : 'pending'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {sel.items.filter((it) => !it.milestone_id).length > 0 && (
              <div style={{ ...box, padding: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 4 }}>Deal-level (activates on first payment)</div>
                {sel.items.filter((it) => !it.milestone_id).map((it) => (
                  <div key={it.id} style={{ fontSize: 12.5, display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>{it.label} <span style={{ fontSize: 11, color: '#84929c' }}>({it.kind})</span></span>
                    <span style={{ fontSize: 11, color: it.activated_at ? '#1c9455' : '#9CA3AF' }}>{it.activated_at ? 'activated' : 'pending'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewDeal({ onDone }) {
  const [c, setC] = useState({ email: '', firstName: '', lastName: '' });
  const [withEntity, setWithEntity] = useState(true);
  const [ent, setEnt] = useState({ name: '', legalType: 'delaware_c_corp', countryCode: 'US' });
  const [deal, setDeal] = useState({ title: '', totalValue: '', currency: 'USD', notes: '' });
  const [ms, setMs] = useState([blankMs(), blankMs()]);
  const [busy, setBusy] = useState(false);

  const setMilestone = (i, patch) => setMs((p) => p.map((x, j) => j === i ? { ...x, ...patch } : x));
  const setItem = (mi, ii, patch) => setMs((p) => p.map((x, j) => j === mi ? { ...x, items: x.items.map((it, k) => k === ii ? { ...it, ...patch } : it) } : x));

  const submit = async () => {
    if (!c.email.includes('@')) return alert('Client email is required.');
    if (!deal.title.trim()) return alert('Deal title is required.');
    const milestones = ms.filter((m) => Number(m.amount) > 0).map((m) => ({ amount: m.amount, dueDate: m.dueDate, description: m.description }));
    if (!milestones.length) return alert('Add at least one milestone with an amount.');
    // items carry the 1-based seq of the milestone they belong to
    const items = [];
    let seq = 0;
    for (const m of ms) {
      if (!(Number(m.amount) > 0)) continue;
      seq += 1;
      for (const it of m.items) if (it.label.trim()) items.push({ ...it, milestoneSeq: seq });
    }
    setBusy(true);
    try {
      await naytiveAPI.createDeal({
        client: c,
        entity: withEntity && ent.name.trim() ? ent : undefined,
        deal: { ...deal, totalValue: deal.totalValue ? Number(deal.totalValue) : undefined },
        items,
        milestones,
      });
      alert('Deal created and client onboarded. Open the deal to send the first payment link.');
      onDone();
    } catch (err) { alert(err.response?.data?.message || 'Could not create the deal'); }
    finally { setBusy(false); }
  };

  const upd = (setter) => (k, v) => setter((p) => ({ ...p, [k]: v }));
  return (
    <div style={{ ...box, padding: 18 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 15 }}>New deal</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lab}>Client email *</label><input style={inp} value={c.email} onChange={(e) => upd(setC)('email', e.target.value)} placeholder="founder@company.com" /></div>
        <div><label style={lab}>First name</label><input style={inp} value={c.firstName} onChange={(e) => upd(setC)('firstName', e.target.value)} /></div>
        <div><label style={lab}>Last name</label><input style={inp} value={c.lastName} onChange={(e) => upd(setC)('lastName', e.target.value)} /></div>
      </div>

      <label style={{ ...lab, display: 'flex', alignItems: 'center', gap: 7, textTransform: 'none', fontSize: 13 }}>
        <input type="checkbox" checked={withEntity} onChange={(e) => setWithEntity(e.target.checked)} /> Create / attach a company for this deal
      </label>
      {withEntity && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 90px', gap: 12, margin: '8px 0 14px' }}>
          <div><label style={lab}>Company name</label><input style={inp} value={ent.name} onChange={(e) => upd(setEnt)('name', e.target.value)} placeholder="Acme Labs Inc." /></div>
          <div><label style={lab}>Legal type</label><select style={inp} value={ent.legalType} onChange={(e) => upd(setEnt)('legalType', e.target.value)}>{LEGAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div><label style={lab}>Country</label><input style={inp} value={ent.countryCode} onChange={(e) => upd(setEnt)('countryCode', e.target.value.toUpperCase())} maxLength={2} /></div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px', gap: 12, marginBottom: 6 }}>
        <div><label style={lab}>Deal title *</label><input style={inp} value={deal.title} onChange={(e) => upd(setDeal)('title', e.target.value)} placeholder="Compliance Pro (offline)" /></div>
        <div><label style={lab}>Total value</label><input style={inp} type="number" value={deal.totalValue} onChange={(e) => upd(setDeal)('totalValue', e.target.value)} placeholder="3000" /></div>
        <div><label style={lab}>Currency</label><input style={inp} value={deal.currency} onChange={(e) => upd(setDeal)('currency', e.target.value.toUpperCase())} maxLength={3} /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lab}>Notes</label><input style={inp} value={deal.notes} onChange={(e) => upd(setDeal)('notes', e.target.value)} placeholder="e.g. 450 now, 450 in 3 months…" /></div>

      {/* Per-payment: each milestone carries its own included items */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={lab}>Payment schedule — each payment with what it includes *</label>
        <button style={{ ...btnGhost, padding: '4px 10px' }} onClick={() => setMs((p) => [...p, blankMs()])}>+ payment</button>
      </div>
      {ms.map((m, mi) => (
        <div key={mi} style={{ ...box, padding: 12, marginBottom: 10, background: '#fbfcfd' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 150px 1fr 30px', gap: 8 }}>
            <input style={inp} type="number" placeholder="Amount" value={m.amount} onChange={(e) => setMilestone(mi, { amount: e.target.value })} />
            <input style={inp} type="date" value={m.dueDate} onChange={(e) => setMilestone(mi, { dueDate: e.target.value })} />
            <input style={inp} placeholder="Note (e.g. Setup + Q1 filings)" value={m.description} onChange={(e) => setMilestone(mi, { description: e.target.value })} />
            <button style={{ ...btnGhost, padding: 0 }} onClick={() => setMs((p) => p.filter((_, j) => j !== mi))}>×</button>
          </div>
          <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #e0f0f1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#9CA3AF' }}>Included when this is paid</span>
              <button style={{ ...btnGhost, padding: '3px 9px', fontSize: 12 }} onClick={() => setMilestone(mi, { items: [...m.items, blankItem()] })}>+ item</button>
            </div>
            {m.items.map((it, ii) => (
              <div key={ii} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 150px 28px', gap: 7, marginBottom: 5 }}>
                <select style={inp} value={it.kind} onChange={(e) => setItem(mi, ii, { kind: e.target.value })}>{ITEM_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select>
                <input style={inp} placeholder="Label (e.g. Delaware Franchise Tax)" value={it.label} onChange={(e) => setItem(mi, ii, { label: e.target.value })} />
                <input style={inp} placeholder="ref (filing code / condition)" value={it.refCode} onChange={(e) => setItem(mi, ii, { refCode: e.target.value })} />
                <button style={{ ...btnGhost, padding: 0 }} onClick={() => setMilestone(mi, { items: m.items.filter((_, k) => k !== ii) })}>×</button>
              </div>
            ))}
            {m.items.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF' }}>No items — add what this payment covers.</div>}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 12 }}>
        item kinds: <b>filing</b> (ref = a filing code, e.g. DE_FRANCHISE — appears on the client calendar as “Naytive handling”) · <b>compliance_pro</b> (turns the plan on) · <b>condition</b> (ref = a spec condition, adds its filings) · <b>service/custom</b> (recorded, ops fulfils).
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btn} disabled={busy} onClick={submit}>{busy ? 'Creating…' : 'Create deal & onboard client'}</button>
      </div>
    </div>
  );
}
